import { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format, startOfWeek } from "date-fns";
import { fr } from "date-fns/locale";
import {
  Box,
  Typography,
  Grid,
  Card,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  Person,
  Phone,
  Email,
  LocationOn,
  Print,
  Edit,
  Cancel,
  History,
  ReceiptLong,
  CheckCircle,
  AssignmentInd,
  LocalShipping,
  Schedule,
  ContactPhone,
  Payment,
  PersonPin,
} from "@mui/icons-material";
import {
  Button,
  LoadingSpinner,
  ErrorMessage,
  ConfirmDialog,
} from "@/components/ui";
import { orderService, Order, OrderStatus } from "@/services/order.service";
import { weeklyMenuService } from "@/services/weeklyMenu.service";

function parseLocalDateOnlyOrderDetail(dateStr: string): Date {
  const s = dateStr.slice(0, 10);
  const [y, m, d] = s.split("-").map(Number);
  if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) return new Date(NaN);
  return new Date(y, m - 1, d, 12, 0, 0, 0);
}

function toSlotDateKeyOrderDetail(date: string | Date): string {
  if (typeof date === "string") return date.slice(0, 10);
  return new Date(date).toISOString().slice(0, 10);
}
import { useToastStore } from "@/store/toastStore";
import { CustomerTypeBadge } from "@/components/ui/CustomerTypeBadge";
import { formatPaymentMethodLabel } from "@/utils/formatPaymentMethodLabel";
import { formatCustomerName, getCustomerInitials } from "@/utils/formatCustomerName";

interface TimelineStep {
  id: string;
  label: string;
  description: string;
  timestamp?: string;
  /** Nom de la personne ayant réalisé l'action (ex. créateur de la commande) */
  actorName?: string;
  icon: React.ReactNode;
  completed: boolean;
  active?: boolean;
}

/**
 * Page de détails d'une commande
 * Basée sur la maquette order_details_&_lifecycle_timeline
 */
export function OrderDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { success, error: showError } = useToastStore();

  const [cancelConfirm, setCancelConfirm] = useState<boolean>(false);

  // Récupérer la commande
  const {
    data: order,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["orders", id],
    queryFn: () => orderService.getById(id!),
    enabled: !!id,
  });

  const deliveryDateKey = order?.deliveryDate?.slice(0, 10) ?? null;
  const menuWeekStartForSwitch = useMemo(() => {
    if (!deliveryDateKey) return null;
    return format(startOfWeek(parseLocalDateOnlyOrderDetail(deliveryDateKey), { weekStartsOn: 1 }), "yyyy-MM-dd");
  }, [deliveryDateKey]);

  const { data: weeklyMenuForSwitch } = useQuery({
    queryKey: ["weekly-menu", "order-detail", menuWeekStartForSwitch],
    queryFn: () => weeklyMenuService.getByWeekIfExists(menuWeekStartForSwitch!),
    enabled: !!menuWeekStartForSwitch,
    staleTime: 60_000,
  });

  const productIdsSwitchFromMenu = useMemo(() => {
    const set = new Set<string>();
    if (!deliveryDateKey || !weeklyMenuForSwitch?.slots?.length) return set;
    for (const s of weeklyMenuForSwitch.slots) {
      if (s.slotType !== "PLATS" || !s.isSwitch || !s.productId) continue;
      if (toSlotDateKeyOrderDetail(s.date as string | Date) !== deliveryDateKey) continue;
      set.add(s.productId);
    }
    return set;
  }, [deliveryDateKey, weeklyMenuForSwitch]);

  // Mutation pour annuler la commande
  const cancelMutation = useMutation({
    mutationFn: () => orderService.cancel(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orders", id] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      success("Commande annulée avec succès");
      setCancelConfirm(false);
    },
    onError: (err: any) => {
      showError(
        err?.response?.data?.message ||
          "Erreur lors de l'annulation de la commande",
      );
    },
  });

  // Générer les étapes de la timeline selon le statut
  const generateTimeline = (order: Order): TimelineStep[] => {
    const statusHistoryByStatus = new Map<OrderStatus, string>();
    for (const entry of order.statusHistory ?? []) {
      if (!statusHistoryByStatus.has(entry.status)) {
        statusHistoryByStatus.set(entry.status, entry.createdAt);
      }
    }
    const getStatusTimestamp = (status: OrderStatus): string | undefined => {
      const ts = statusHistoryByStatus.get(status);
      return ts ? format(new Date(ts), "HH:mm") : undefined;
    };

    const createdByName =
      order.createdByUser?.firstName || order.createdByUser?.lastName
        ? [order.createdByUser?.firstName, order.createdByUser?.lastName]
            .filter(Boolean)
            .join(" ")
        : undefined;
    const steps: TimelineStep[] = [
      {
        id: "created",
        label: "Commande Créée",
        description: "Commande reçue via le site web.",
        timestamp: format(new Date(order.createdAt), "HH:mm"),
        actorName: createdByName,
        icon: <ReceiptLong />,
        completed: true,
      },
      {
        id: "confirmed",
        label: "Confirmée",
        description: "Validée par l'équipe cuisine.",
        timestamp: getStatusTimestamp("CONFIRMED"),
        icon: <CheckCircle />,
        completed: [
          "CONFIRMED",
          "IN_PREPARATION",
          "READY",
          "IN_DELIVERY",
          "DELIVERED",
        ].includes(order.status),
      },
      {
        id: "preparation",
        label: "Affectée",
        description: "Commande affectée à un livreur.",
        timestamp: [
          "IN_PREPARATION",
          "READY",
          "IN_DELIVERY",
          "DELIVERED",
        ].includes(order.status)
          ? getStatusTimestamp("IN_PREPARATION")
          : undefined,
        icon: <AssignmentInd />,
        completed: [
          "IN_PREPARATION",
          "READY",
          "IN_DELIVERY",
          "DELIVERED",
        ].includes(order.status),
        active: order.status === "IN_PREPARATION",
      },
      {
        id: "ready",
        label: "Prête",
        description: "Le livreur a confirmé la préparation.",
        timestamp: ["READY", "IN_DELIVERY", "DELIVERED"].includes(order.status)
          ? getStatusTimestamp("READY")
          : undefined,
        icon: <Schedule />,
        completed: ["READY", "IN_DELIVERY", "DELIVERED"].includes(order.status),
        active: order.status === "READY",
      },
      {
        id: "delivery",
        label: "En Livraison",
        description: "Le livreur est en route vers le client.",
        timestamp: ["IN_DELIVERY", "DELIVERED"].includes(order.status)
          ? getStatusTimestamp("IN_DELIVERY")
          : undefined,
        icon: <LocalShipping />,
        completed: ["IN_DELIVERY", "DELIVERED"].includes(order.status),
        active: order.status === "IN_DELIVERY",
      },
      {
        id: "delivered",
        label: "Livrée",
        description: "Commande livrée avec succès.",
        timestamp:
          order.status === "DELIVERED"
            ? getStatusTimestamp("DELIVERED")
            : undefined,
        icon: <CheckCircle />,
        completed: order.status === "DELIVERED",
        active: order.status === "DELIVERED",
      },
    ];

    return steps;
  };

  const getStatusBadgeProps = (status: OrderStatus) => {
    const statusMap: Record<
      OrderStatus,
      {
        label: string;
        color:
          | "primary"
          | "secondary"
          | "success"
          | "error"
          | "warning"
          | "info";
      }
    > = {
      PENDING: { label: "En Attente", color: "warning" },
      CONFIRMED: { label: "Confirmée", color: "info" },
      IN_PREPARATION: { label: "En Préparation", color: "info" },
      READY: { label: "Prête", color: "primary" },
      IN_DELIVERY: { label: "En Livraison", color: "primary" },
      DELIVERED: { label: "Livrée", color: "success" },
      CANCELLED: { label: "Annulée", color: "error" },
    };

    return statusMap[status] || { label: status, color: "default" };
  };

  const handleCancelClick = () => {
    if (order?.status === "DELIVERED") {
      showError("Impossible d'annuler une commande déjà livrée");
      return;
    }
    if (order?.status === "CANCELLED") {
      showError("La commande est déjà annulée");
      return;
    }
    setCancelConfirm(true);
  };

  const handleCancelConfirm = () => {
    cancelMutation.mutate();
  };

  const handlePrint = () => {
    window.print();
  };

  if (isLoading) {
    return <LoadingSpinner message="Chargement de la commande..." />;
  }

  if (error) {
    return <ErrorMessage message="Erreur lors du chargement de la commande." />;
  }

  if (!order) {
    return <ErrorMessage message="Commande non trouvée." />;
  }

  const statusBadge = getStatusBadgeProps(order.status);
  const timelineSteps = generateTimeline(order);

  // Adresse de livraison pour carte et lien GPS
  const deliveryAddress = order.customerAddress?.address ?? order.customer?.address ?? "";
  const deliveryPostalCode = order.customerAddress?.postalCode ?? order.customer?.postalCode ?? "";
  const deliveryCity = order.customerAddress?.city ?? order.customer?.city ?? "";
  const fullAddressParts = [deliveryAddress, deliveryPostalCode, deliveryCity].filter(Boolean);
  const fullAddress = fullAddressParts.join(", ");
  const hasCoords =
    order.customerAddress?.latitude != null && order.customerAddress?.longitude != null;
  const lat = order.customerAddress?.latitude ?? 0;
  const lon = order.customerAddress?.longitude ?? 0;
  const googleMapsUrl = fullAddress
    ? hasCoords
      ? `https://www.google.com/maps?q=${lat},${lon}`
      : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`
    : "";
  const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? "";
  const googleMapsEmbedQuery =
    hasCoords && lat && lon ? `${lat},${lon}` : fullAddress || "";
  const googleMapsEmbedUrl =
    googleMapsApiKey && googleMapsEmbedQuery
      ? `https://www.google.com/maps/embed/v1/place?key=${googleMapsApiKey}&q=${encodeURIComponent(googleMapsEmbedQuery)}&zoom=15`
      : "";

  const subtotalProducts = order.items.reduce(
    (sum, item) => sum + Number(item.subtotal),
    0,
  );
  const deliveryCostNum = order.serviceType === "CATERING" ? 0 : (Number(order.deliveryCost) || 0);
  const discountNum = Number(order.discountAmount) || 0;
  const total = Number(order.totalAmount);
  const amountHt = order.totalAmountHt != null ? Number(order.totalAmountHt) : null;
  const vatAmountStored = order.vatAmount != null ? Number(order.vatAmount) : null;
  const vatRateStored = order.vatRate != null ? Number(order.vatRate) : null;
  const vatAmount =
    vatAmountStored ??
    (amountHt != null && vatRateStored != null
      ? Math.round(amountHt * vatRateStored * 100) / 100
      : total - total / (1 + (vatRateStored ?? 0.085)));
  const vatRatePercent = (vatRateStored != null ? vatRateStored * 100 : 8.5).toFixed(1);
  const SERVICE_TYPE_LABELS: Record<string, string> = {
    MEAL_DELIVERY: "Livraison",
    CATERING: "Traiteur",
  };
  const RECURRENCE_LABELS: Record<string, string> = {
    ONCE: "Ponctuelle",
    DAILY: "Quotidienne",
    WEEKLY: "Hebdomadaire",
    MONTHLY: "Mensuelle",
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pb: 4 }}>
      {/* Header: Titre + Actions */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", lg: "center" },
          gap: 3,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "28px", md: "36px" },
                fontWeight: 900,
                color: "primary.main",
              }}
            >
              Commande #{order.orderNumber}
            </Typography>
            <Chip
              label={statusBadge.label}
              color={statusBadge.color}
              icon={<LocalShipping />}
              sx={{
                fontWeight: 700,
                fontSize: "14px",
                height: 32,
                ...(order.status === "IN_DELIVERY" && {
                  animation: "pulse 2s infinite",
                }),
              }}
            />
          </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Schedule sx={{ fontSize: 16, color: "text.secondary" }} />
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Livraison prévue : {format(new Date(order.deliveryDate), "d MMMM yyyy", {
                    locale: fr,
                  })}
                  {order.deliveryTimeSlot ? ` · ${order.deliveryTimeSlot}` : ""}
                </Typography>
              </Box>
              {order.createdByUser && (
                <>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>|</Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Créé par {order.createdByUser.firstName} {order.createdByUser.lastName}
                  </Typography>
                </>
              )}
              {order.serviceType && (
                <>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>|</Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {SERVICE_TYPE_LABELS[order.serviceType] ?? order.serviceType}
                  </Typography>
                </>
              )}
              {order.recurrence && (
                <>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>|</Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {RECURRENCE_LABELS[order.recurrence] ?? order.recurrence}
                  </Typography>
                </>
              )}
            </Box>
        </Box>

        {/* Actions */}
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <Button
            variant="outline"
            startIcon={<Print />}
            onClick={handlePrint}
          >
            Imprimer
          </Button>
          {order.status !== "DELIVERED" && order.status !== "CANCELLED" && (
            <Button
              variant="outline"
              startIcon={<Edit />}
              onClick={() => navigate(`/orders/${order.id}/edit`)}
            >
              Modifier
            </Button>
          )}
          {order.status !== "DELIVERED" && order.status !== "CANCELLED" && (
            <Button
              variant="danger"
              startIcon={<Cancel />}
              onClick={handleCancelClick}
              disabled={cancelMutation.isPending}
            >
              Annuler
            </Button>
          )}
        </Box>
      </Box>

      {/* Grid: Client & Items (gauche) + Timeline (droite) */}
      <Grid container spacing={3}>
        {/* Colonne gauche : Client & Items */}
        <Grid item xs={12} xl={8}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Carte Informations Client */}
            <Card>
              <Box
                sx={{
                  p: 3,
                  borderBottom: 1,
                  borderColor: "divider",
                  bgcolor: "grey.50",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: "18px",
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Person sx={{ color: "primary.main" }} />
                    Informations Client
                  </Typography>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => navigate(`/customers/${order.customerId}`)}
                  >
                    Voir profil
                  </Button>
                </Box>
              </Box>
              <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  {/* Info Client */}
                  <Grid item xs={12} md={7}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Avatar
                          sx={{
                            width: 56,
                            height: 56,
                            bgcolor: "primary.main",
                            fontSize: "20px",
                            fontWeight: 700,
                          }}
                        >
                          {getCustomerInitials({
                            firstName: order.customer?.firstName,
                            lastName: order.customer?.lastName ?? "",
                            type: order.customer?.type as
                              | "INDIVIDUAL"
                              | "INSTITUTION"
                              | "COMPANY"
                              | undefined,
                          }).charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography
                            variant="h4"
                            sx={{ fontSize: "18px", fontWeight: 700 }}
                          >
                            {formatCustomerName({
                              firstName: order.customer?.firstName,
                              lastName: order.customer?.lastName ?? "",
                              type: order.customer?.type as
                                | "INDIVIDUAL"
                                | "INSTITUTION"
                                | "COMPANY"
                                | undefined,
                            }) || "N/A"}
                          </Typography>
                          {order.customer?.type && (
                            <CustomerTypeBadge
                              type={order.customer.type as any}
                              size="small"
                              sx={{ mt: 0.5 }}
                            />
                          )}
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.5,
                        }}
                      >
                        {order.customer?.phone && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Phone
                              sx={{ fontSize: 20, color: "text.secondary" }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {order.customer.phone}
                            </Typography>
                          </Box>
                        )}

                        {order.customer?.email && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Email
                              sx={{ fontSize: 20, color: "text.secondary" }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {order.customer.email}
                            </Typography>
                          </Box>
                        )}

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1,
                          }}
                        >
                          <LocationOn
                            sx={{
                              fontSize: 20,
                              color: "text.secondary",
                              mt: 0.5,
                            }}
                          />
                          <Box>
                            {(order.customerAddress?.address ?? order.customer?.address) && (
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {order.customerAddress?.address ?? order.customer?.address}
                              </Typography>
                            )}
                            <Typography
                              variant="body2"
                              sx={{ color: "text.secondary" }}
                            >
                              {order.customerAddress?.postalCode ?? order.customer?.postalCode}{" "}
                              {order.customerAddress?.city ?? order.customer?.city}
                              {order.customerAddress?.zone &&
                                `, ${order.customerAddress.zone.name}`}
                            </Typography>
                            {order.specialInstructions && (
                              <Chip
                                label={`Instructions: ${order.specialInstructions}`}
                                size="small"
                                sx={{
                                  mt: 1,
                                  fontSize: "11px",
                                  bgcolor: "warning.light",
                                  color: "warning.dark",
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Carte et lien GPS */}
                  <Grid item xs={12} md={5}>
                    <Box
                      sx={{
                        width: "100%",
                        height: 200,
                        borderRadius: 2,
                        overflow: "hidden",
                        position: "relative",
                        bgcolor: "grey.200",
                      }}
                    >
                      {googleMapsEmbedUrl ? (
                        <iframe
                          title="Carte de l'adresse de livraison"
                          src={googleMapsEmbedUrl}
                          width="100%"
                          height="100%"
                          style={{
                            border: 0,
                            display: "block",
                          }}
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                        />
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            p: 2,
                          }}
                        >
                          <LocationOn sx={{ fontSize: 48, color: "grey.400" }} />
                          <Typography
                            variant="caption"
                            sx={{ color: "text.secondary", mt: 1, textAlign: "center" }}
                          >
                            Aperçu carte non disponible
                            {fullAddress ? " (coordonnées non renseignées)" : ""}
                          </Typography>
                        </Box>
                      )}
                      {googleMapsUrl && (
                        <Button
                          component="a"
                          href={googleMapsUrl}
                          {...({ target: "_blank", rel: "noopener noreferrer" } as Pick<React.AnchorHTMLAttributes<HTMLAnchorElement>, "target" | "rel">)}
                          variant="primary"
                          size="small"
                          startIcon={<LocationOn />}
                          sx={{
                            position: "absolute",
                            bottom: 8,
                            right: 8,
                            boxShadow: 2,
                          }}
                        >
                          Ouvrir dans le GPS
                        </Button>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Card>

            {/* Paiement et Livraison */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Card sx={{ height: "100%" }}>
                  <Box sx={{ p: 3, borderBottom: 1, borderColor: "divider", bgcolor: "grey.50" }}>
                    <Typography variant="h3" sx={{ fontSize: "18px", fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
                      <Payment sx={{ color: "primary.main" }} />
                      Paiement
                    </Typography>
                  </Box>
                  <Box sx={{ p: 3 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                      <Box>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>Mode</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {order.paymentMethod
                            ? formatPaymentMethodLabel(order.paymentMethod)
                            : "—"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>État</Typography>
                        <Box sx={{ mt: 0.5 }}>
                          {order.paymentStatus ? (
                            <Chip
                              label={order.paymentStatus.name}
                              size="small"
                              sx={{
                                bgcolor: order.paymentStatus.colorCode ?? "grey.200",
                                color: "grey.800",
                                fontWeight: 600,
                              }}
                            />
                          ) : (
                            <Typography variant="body2">—</Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card sx={{ height: "100%" }}>
                  <Box sx={{ p: 3, borderBottom: 1, borderColor: "divider", bgcolor: "grey.50" }}>
                    <Typography variant="h3" sx={{ fontSize: "18px", fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
                      <LocalShipping sx={{ color: "primary.main" }} />
                      Livraison
                    </Typography>
                  </Box>
                  <Box sx={{ p: 3 }}>
                    {order.delivery ? (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        {order.delivery.driver && (
                          <Box>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>Livreur</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {order.delivery.driver.firstName} {order.delivery.driver.lastName}
                            </Typography>
                          </Box>
                        )}
                        {order.delivery.zone && (
                          <Box>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>Zone</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {order.delivery.zone.name}
                            </Typography>
                          </Box>
                        )}
                        <Box>
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>Statut livraison</Typography>
                          <Box sx={{ mt: 0.5 }}>
                            <Chip
                              label={
                                order.delivery.status === "PLANNED"
                                  ? "Planifiée"
                                  : order.delivery.status === "IN_PROGRESS"
                                    ? "En cours"
                                    : order.delivery.status === "DELIVERED"
                                      ? "Livrée"
                                      : order.delivery.status
                              }
                              size="small"
                              color={order.delivery.status === "DELIVERED" ? "success" : "default"}
                            />
                          </Box>
                        </Box>
                        {order.delivery.scheduledDeliveryTime && (
                          <Box>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>Créneau prévu</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {format(new Date(order.delivery.scheduledDeliveryTime), "HH:mm")}
                            </Typography>
                          </Box>
                        )}
                        <Button
                          variant="text"
                          size="small"
                          startIcon={<PersonPin />}
                          onClick={() => navigate("/deliveries")}
                          sx={{ mt: 1, alignSelf: "flex-start" }}
                        >
                          Voir le planning
                        </Button>
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Livraison non planifiée
                      </Typography>
                    )}
                  </Box>
                </Card>
              </Grid>
            </Grid>

            {/* Récapitulatif de la commande */}
            <Card>
              <Box
                sx={{
                  p: 3,
                  borderBottom: 1,
                  borderColor: "divider",
                  bgcolor: "grey.50",
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: "18px",
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <ReceiptLong sx={{ color: "primary.main" }} />
                  Récapitulatif de la commande
                </Typography>
              </Box>
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      Sous-total produits
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {subtotalProducts.toFixed(2)} €
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      Livraison
                      {order.serviceType === "CATERING" && (
                        <Typography component="span" variant="caption" sx={{ ml: 0.5, color: "text.secondary" }}>
                          (A emporter)
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {deliveryCostNum.toFixed(2)} €
                    </Typography>
                  </Box>
                  {discountNum > 0 && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Réduction
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "success.main" }}>
                        -{discountNum.toFixed(2)} €
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      TVA ({vatRatePercent} %)
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {vatAmount.toFixed(2)} €
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      pt: 2,
                      mt: 1,
                      borderTop: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "primary.main" }}>
                      Total à payer
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "primary.main" }}>
                      {total.toFixed(2)} €
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
                  Détail des articles
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "grey.50" }}>
                        <TableCell sx={{ fontWeight: 600, fontSize: "12px" }}>Produit</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, fontSize: "12px" }}>Prix unit.</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, fontSize: "12px" }}>Qté</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, fontSize: "12px" }}>Sous-total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {order.items.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {item.product?.name || "Produit"}
                              </Typography>
                              {productIdsSwitchFromMenu.has(item.productId) && (
                                <Chip
                                  label="Switch"
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: "0.7rem",
                                    fontWeight: 700,
                                    bgcolor: "#fef3c7",
                                    color: "#b45309",
                                    border: "1px solid #fde68a",
                                  }}
                                />
                              )}
                            </Box>
                            {(item.notes || item.options) && (
                              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                {item.options || item.notes}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">{Number(item.unitPrice).toFixed(2)} €</TableCell>
                          <TableCell align="center">{item.quantity}</TableCell>
                          <TableCell align="right">{Number(item.subtotal).toFixed(2)} €</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Card>
          </Box>
        </Grid>

        {/* Colonne droite : Timeline */}
        <Grid item xs={12} xl={4}>
          <Card sx={{ position: { xl: "sticky" }, top: 96 }}>
            <Box
              sx={{
                p: 3,
                borderBottom: 1,
                borderColor: "divider",
                bgcolor: "grey.50",
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontSize: "18px",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <History sx={{ color: "primary.main" }} />
                Chronologie
              </Typography>
            </Box>
            <Box sx={{ p: 3 }}>
              <Box
                sx={{
                  position: "relative",
                  pl: 4,
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: 15,
                    width: 2,
                    bgcolor: "divider",
                    zIndex: 0,
                  },
                }}
              >
                {timelineSteps.map((step) => (
                  <Box
                    key={step.id}
                    sx={{
                      position: "relative",
                      zIndex: 1,
                      display: "flex",
                      gap: 2,
                      mb: 4,
                      "&:last-child": { mb: 0 },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: step.completed
                            ? step.active
                              ? "primary.main"
                              : "primary.main"
                            : "grey.300",
                          color: "white",
                          boxShadow: step.active ? 3 : 1,
                          ...(step.active && {
                            animation: "pulse 2s infinite",
                          }),
                        }}
                      >
                        {step.icon}
                      </Box>
                    </Box>
                    <Box sx={{ flex: 1, pt: 0.5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            color: step.active
                              ? "primary.main"
                              : "text.primary",
                          }}
                        >
                          {step.label}
                        </Typography>
                        {step.timestamp && (
                          <Chip
                            label={step.timestamp}
                            size="small"
                            sx={{
                              fontSize: "10px",
                              height: 20,
                              fontFamily: "monospace",
                              bgcolor: step.active
                                ? "primary.main"
                                : "grey.100",
                              color: step.active ? "white" : "text.secondary",
                            }}
                          />
                        )}
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          display: "block",
                          mb: step.actorName ? 0.5 : 1,
                        }}
                      >
                        {step.description}
                      </Typography>
                      {step.actorName && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontStyle: "italic",
                            display: "block",
                            mb: 1,
                          }}
                        >
                          Par {step.actorName}
                        </Typography>
                      )}
                      {step.active && step.id === "delivery" && (
                        <Button
                          variant="primary"
                          size="small"
                          startIcon={<ContactPhone />}
                          sx={{ fontSize: "12px", mt: 1 }}
                        >
                          Contacter
                        </Button>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog de confirmation d'annulation */}
      <ConfirmDialog
        open={cancelConfirm}
        title="Annuler la commande"
        message={`Êtes-vous sûr de vouloir annuler la commande ${order.orderNumber} ? Cette action est irréversible.`}
        confirmText="Annuler la commande"
        cancelText="Retour"
        variant="danger"
        onConfirm={handleCancelConfirm}
        onCancel={() => setCancelConfirm(false)}
        loading={cancelMutation.isPending}
      />
    </Box>
  );
}
